import React, {useContext, useState} from 'react';
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import {FormattedMessage, injectIntl} from "react-intl";
import {getLocalizedMessage} from "../../../util/Localization";
import Row from "@amzn/meridian/row";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import {
    checkContainerDestinationConflicts,
    checkDuplicateScannables,
    checkZancaSortAddTestPackageModalValidations,
    isValidEntry,
    PATTERNS
} from "../../../util/Validations";
import Input from "@amzn/meridian/input";
import {getTestConfigKey} from "../../../util/Util";
import {CONFIG_TYPE, MERIDIAN_ALERT_TYPE} from "../../../util/Constants";
import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import {FEATURES, FeatureUtility} from '../../../util/FeatureUtility';
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";

const AddZancaSortTestPackageModal = (props) => {

    const {mac, intl} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext);
    const {showCustomAlert} = useContext(GlobalStateContext);
    const isNewZancasortTestConfigEnabled = FeatureUtility.getFeatureStatus(FEATURES.IS_NEW_ZANCASORT_TEST_CONFIG_ENABLED);

    // Visual controls
    const {open, setOpen} = props;
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false);

    // Values
    const [scannables, setScannables] = useState("");
    const [destination, setDestination] = useState("");
    const [containerList, setContainerList] = useState("");

    // Error
    const [scannablesErr, setScannablesErr] = useState(false);
    const [destinationErr, setDestinationErr] = useState(false);
    const [containerListErr, setContainerListErr] = useState(false);

    const setConfigData = (stowTestPackage, mac) => {
        if(isNewZancasortTestConfigEnabled) {
            const existingData = getDraftData({
                stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
                subKey: mac
            });

            const currentKey = edit ? getTestConfigKey(stowTestPackage.scannables) : null;

            const duplicateScannables = checkDuplicateScannables(
                stowTestPackage.scannables,
                existingData,
                currentKey
            );

            if (duplicateScannables.length > 0) {
                const errorMsg = (
                    <FormattedMessage
                        id="testconfig.error.duplicatescannables"
                        values={{ scannableIds: duplicateScannables.join(", ") }}
                        defaultMessage="Duplicate scannables found: {scannableIds}"
                    />);
                showCustomAlert(errorMsg, MERIDIAN_ALERT_TYPE.ERROR);
                return;
            }

            const duplicateContainers = checkContainerDestinationConflicts(
                stowTestPackage.containerList,
                stowTestPackage.destination,
                existingData,
                currentKey
            );

            if (duplicateContainers.length > 0) {
                const errorMsg = (
                    <FormattedMessage
                        id="testconfig.error.duplicatebagtosz"
                        values={{ containerId: duplicateContainers }}
                        defaultMessage="Bag is already mapped to a different sort zone."
                    />
                );
                showCustomAlert(errorMsg, MERIDIAN_ALERT_TYPE.ERROR);
                return;
            }
        }

        if (edit) {
            if(isNewZancasortTestConfigEnabled) {
                editInstance({
                    stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
                    subKey: mac,
                    instance: stowTestPackage,
                    keyComparator: (config1, config2) => (
                        getTestConfigKey(config1.scannables) === getTestConfigKey(config2.scannables)
                    ),
                    valueComparator: (config1, config2) => {
                        return (config1.destination === config2.destination &&
                            getTestConfigKey(config1.containerList) === getTestConfigKey(config2.containerList)
                        );
                    }
                });
            } else {
                editInstance({
                    stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
                    subKey: mac,
                    instance: stowTestPackage,
                    keyComparator: (config1, config2) => (
                        config1.destination === config2.destination
                    ),
                    valueComparator: (config1, config2) => {
                        return getTestConfigKey(config1.scannables) === getTestConfigKey(config2.scannables)
                    }
                });
            }
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
                subKey: mac,
                instance: stowTestPackage
            });
        }
    }

    const stowTestPackage = isNewZancasortTestConfigEnabled ? {
        scannables: scannables.split(';').filter(Boolean),
        destination: destination,
        containerList: containerList ? containerList.split(';').filter(Boolean) : []
    } : {
        scannables: scannables.split(';').filter(Boolean),
        destination: destination
    }

    const testConfigMap = {}
    getDraftData({stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION, subKey: mac}).forEach((value) => {
        if (isNewZancasortTestConfigEnabled) {
            testConfigMap[value.destination] = {
                scannables: getTestConfigKey(value.scannables),
                containerList: Array.isArray(value.containerList) ? value.containerList.join(';') : ''
            };
        } else {
            testConfigMap[value.destination] = {
                scannables: getTestConfigKey(value.scannables)
            };
        }
    });

    const resetConfigDetails = () => {
        if (isNewZancasortTestConfigEnabled) {
            setDestination("");
            setContainerList("");
        } else {
            setDestination("");
        }
    }

    const resetConfigErrorDetails = () => {
        if (isNewZancasortTestConfigEnabled) {
            setDestinationErr(false);
            setContainerListErr(false);
        } else {
            setDestinationErr(false);
        }
    }

    function resetInputState() {
        setScannables("");
        resetConfigDetails();
        resetConfigErrorDetails();
    }

    function onCloseModal() {
        setShow(false);
        setOpen(false);
        resetInputState();
    }

    const prefill = () => {
        if (isNewZancasortTestConfigEnabled) {
            // Get all existing data
            const existingData = getDraftData({
                stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
                subKey: mac
            });

            // Look for entry with exactly matching scannables
            const matchingEntry = existingData.find(entry =>
                getTestConfigKey(entry.scannables) === getTestConfigKey(scannables.split(';').filter(Boolean))
            );

            if (matchingEntry) {
                setEdit(true);
                resetConfigErrorDetails();
                setDestination(matchingEntry.destination || '');
                setContainerList(Array.isArray(matchingEntry.containerList) ? matchingEntry.containerList.join(';') : '');
            } else {
                setEdit(false);
                resetConfigDetails();
            }
        } else {
            const key = stowTestPackage.destination;
            if (testConfigMap.hasOwnProperty(key)) {
                setEdit(true);
                resetConfigErrorDetails();
                setScannables(testConfigMap[key].scannables);
            } else if (edit) {
                setEdit(false);
                resetConfigDetails()
            }
        }
    }

    const onClickConfirm = (stowTestPackage, mac) => {
        if (checkZancaSortAddTestPackageModalValidations(stowTestPackage)) {
            setConfigData(stowTestPackage, mac);
            setOpen(false);
            resetInputState();
            setShow(false);
        } else {
            setShow(true);
        }
    }

    return (
        <Row>
            <Modal title={getLocalizedMessage({intl, stringId: "stowtestconfig.addupdate"})}
                   open={open}
                   onClose={onCloseModal}
                   scrollContainer="viewport"
                   closeLabel="Close"
                   bodySpacingInset="medium">
                <Column>
                    <Column spacing="200">
                        <b>
                            {getLocalizedMessage({intl, stringId: "modal.headingtest"})}
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            {getLocalizedMessage({intl, stringId: "test.scannables"})}
                        </Text>
                        <Input id="scannables-input"
                               value={scannables}
                               onChange={(value) => {setScannables(value.replace(/\s/g, ''))}}
                               onBlur={() => {
                                   setScannablesErr(!isValidEntry(scannables, PATTERNS.TEST_SCANNABLE));
                                   if (isNewZancasortTestConfigEnabled) {
                                       prefill()
                                   }
                               }}
                               error={scannablesErr}
                               helperText={getLocalizedMessage({intl, stringId: "test.scannables_description"})}/>
                    </Column>

                    {isNewZancasortTestConfigEnabled ? (
                        [
                            <Column spacing="200">
                                <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                                    {getLocalizedMessage({intl, stringId: "test.sortZone"})}
                                </Text>
                                <Input id="destination-input"
                                       value={destination}
                                       onChange={setDestination}
                                       onBlur={() => {
                                           setDestinationErr(!isValidEntry(destination, PATTERNS.SORT_LOCATION));
                                       }}
                                       error={destinationErr}
                                       helperText={getLocalizedMessage({intl, stringId: "test.destination_sort_zone_description"})}/>
                            </Column>,
                            <Column spacing="200">
                                <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                                    {getLocalizedMessage({intl, stringId: "test.bag_list"})}
                                </Text>
                                <Input id="containers-input"
                                       value={containerList}
                                       onChange={(value) => {setContainerList(value.replace(/\s/g, ''))}}
                                       onBlur={() => {
                                           setContainerListErr(!isValidEntry(containerList, PATTERNS.BAG_LIST));
                                       }}
                                       error={containerListErr}
                                       helperText={getLocalizedMessage({intl, stringId: "test.destination_bag_description"})}/>
                            </Column>
                        ]
                    ) : (
                        <Column spacing="200">
                            <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                                {getLocalizedMessage({intl, stringId: "test.destination"})}
                            </Text>
                            <Input id="destination-input"
                                   value={destination}
                                   onChange={setDestination}
                                   onBlur={() => {
                                       setDestinationErr(!isValidEntry(destination, PATTERNS.BAG_LABEL));
                                       prefill();
                                   }}
                                   error={destinationErr}
                                   helperText={getLocalizedMessage({intl, stringId: "test.destination_bag_description"}) }/>
                        </Column>
                    )}
                </Column>
                <ModalFooter>
                    <Column>
                        {!checkZancaSortAddTestPackageModalValidations(stowTestPackage) &&
                            show &&
                            <Alert type="warning" size="small">
                                {getLocalizedMessage({intl, stringId: "alert.missingfields"})}
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                        </Button>
                        <Button type="primary" size="medium"
                                onClick={() => onClickConfirm(stowTestPackage, mac)}>
                            {edit ? getLocalizedMessage({intl, stringId: "modal.confirmUpdate"})
                                : getLocalizedMessage({intl, stringId: "modal.confirmAdd"})}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )

}
export default injectIntl(AddZancaSortTestPackageModal)


// WEBPACK FOOTER //
// ./src/components/devicemanagement/zancasort/modal/AddZancaSortTestPackageModal.js